// Sending loose clips to the producer's library: one job per new object, a
// few at a time, each with its own progress; a failed one is retried alone
// (one PUT per object, so a retry re-sends that object). Nothing becomes a
// clip until every object is in and /complete confirms them all — so a
// dropped upload leaves the library as it was.
//
// Every job's state is kept and published, with the put's own words when it
// failed: the shelf shows a row per file, and a file that did not arrive
// says what refused it.

import { useCallback, useRef, useState } from "react";

import type { MyClip } from "@shared/api-types.js";
import { putUploadJob, type CompletedFile } from "@shared/upload/publish-seed.js";
import { track } from "@web-shared/lib/analytics";
import { webTransport } from "@web-shared/lib/audiotool-upload";
import type { ClipJobState } from "@/lib/web-upload/clip-progress";
import { completeClipsUpload, openClipsUpload, type ClipsSession, type ClipToSend } from "@/lib/web-upload/library-upload";
import { UploadProblem } from "@/lib/web-upload/publish";
import { useMyClips } from "./useMyClips";

export type ClipsPhase =
  | { kind: "idle" }
  | { kind: "converting"; done: number; total: number }
  | { kind: "sending"; sent: number; total: number; failed: number }
  | { kind: "finishing" }
  | { kind: "done"; clips: MyClip[]; already: string[] }
  | { kind: "failed"; message: string };

const CONCURRENCY = 3;

function messageOf(err: unknown): string {
  if (err instanceof UploadProblem) return err.message;
  if (err instanceof TypeError) return "The connection dropped. Check your internet and try again.";
  return err instanceof Error && err.message ? err.message : "Something went wrong on our side. Try again in a minute.";
}

/** Why one put failed, as the row says it: the words R2 (or the worker)
 * answered with, not the put step's "PUT <label> failed (502): " preamble —
 * the row already names the file. */
function reasonOf(err: unknown): string {
  const m = messageOf(err);
  const stripped = m.replace(/^PUT .* failed \((\d+)\)(?:: )?/, (_all, status: string) => (m.includes(": ") ? "" : `HTTP ${status}`));
  return stripped || m;
}

export function useClipsUpload() {
  const mine = useMyClips();
  const [phase, setPhase] = useState<ClipsPhase>({ kind: "idle" });
  /** The clips handed over, by name, in order — the rows before a session exists. */
  const [names, setNames] = useState<readonly string[]>([]);
  /** The session once the plans are in; what the rows are built from. */
  const [opened, setOpened] = useState<ClipsSession | null>(null);
  /** Every job's state, published on each change. */
  const [jobs, setJobs] = useState<ReadonlyMap<string, ClipJobState>>(new Map());
  /** When the bytes started going over the wire — the time-left estimate's clock. */
  const [sendingSince, setSendingSince] = useState<number | null>(null);
  const session = useRef<ClipsSession | null>(null);
  const completed = useRef(new Map<string, CompletedFile>());
  const jobState = useRef(new Map<string, ClipJobState>());
  const cancelled = useRef(false);
  const via = useRef<"upload" | "sidebar">("upload");

  const setJob = useCallback((id: string, state: ClipJobState) => {
    jobState.current.set(id, state);
    setJobs(new Map(jobState.current));
  }, []);

  const progress = useCallback((failed: number) => {
    const s = session.current;
    if (!s) return;
    const total = s.jobs.reduce((n, j) => n + j.bytes.byteLength, 0);
    const sent = s.jobs.reduce((n, j) => n + (jobState.current.get(j.id)?.sent ?? 0), 0);
    setPhase({ kind: "sending", sent, total, failed });
  }, []);

  const finish = useCallback(async () => {
    const s = session.current;
    if (!s || cancelled.current) return;
    setPhase({ kind: "finishing" });
    try {
      const done = await completeClipsUpload(webTransport, s, s.jobs.map((j) => completed.current.get(j.id)!));
      if (cancelled.current) return;
      mine.added(done.clips, done.usedBytes);
      setPhase({ kind: "done", clips: done.clips, already: s.already });
      track("library_clips_added", { detail: via.current, value: done.clips.length });
    } catch (err) {
      track("web_upload_failed", { detail: err instanceof UploadProblem ? "too_big" : "server" });
      setPhase({ kind: "failed", message: messageOf(err) });
    }
  }, [mine]);

  const send = useCallback(async (ids: string[]) => {
    const s = session.current;
    if (!s) return;
    const queue = s.jobs.filter((j) => ids.includes(j.id));
    for (const job of queue) setJob(job.id, { status: "queued", sent: jobState.current.get(job.id)?.sent ?? 0 });
    let failed = 0;
    setSendingSince((t) => t ?? Date.now());
    progress(0);
    const worker = async (): Promise<void> => {
      for (let job = queue.shift(); job; job = queue.shift()) {
        if (cancelled.current) return;
        const id = job.id;
        setJob(id, { status: "sending", sent: jobState.current.get(id)?.sent ?? 0 });
        try {
          const done = await putUploadJob(webTransport, job, (b) => {
            setJob(id, { status: "sending", sent: b });
            progress(failed);
          });
          completed.current.set(id, done);
          setJob(id, { status: "sent", sent: job.bytes.byteLength });
        } catch (err) {
          failed++;
          setJob(id, { status: "failed", sent: jobState.current.get(id)?.sent ?? 0, reason: reasonOf(err) });
        }
        progress(failed);
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    if (cancelled.current) return;
    if (s.jobs.every((j) => completed.current.has(j.id))) await finish();
    else track("web_upload_failed", { detail: "network" });
  }, [finish, progress, setJob]);

  const start = useCallback(async (clips: ClipToSend[], from: "upload" | "sidebar" = "upload") => {
    cancelled.current = false;
    via.current = from;
    session.current = null;
    completed.current = new Map();
    jobState.current = new Map();
    setJobs(new Map());
    setOpened(null);
    setSendingSince(null);
    setNames(clips.map((c) => c.name));
    setPhase({ kind: "converting", done: 0, total: clips.length });
    try {
      const s = await openClipsUpload(webTransport, clips, (done, total) => setPhase({ kind: "converting", done, total }));
      if (cancelled.current) return;
      session.current = s;
      setOpened(s);
      if (s.jobs.length === 0) await finish();
      else await send(s.jobs.map((j) => j.id));
    } catch (err) {
      if (cancelled.current) return;
      track("web_upload_failed", { detail: err instanceof UploadProblem ? "too_big" : "server" });
      setPhase({ kind: "failed", message: messageOf(err) });
    }
  }, [finish, send]);

  /** Send whatever didn't make it; anything already sent stays sent. A
   * completion that failed is asked again. */
  const retry = useCallback(async () => {
    const s = session.current;
    if (!s) return;
    const left = s.jobs.filter((j) => !completed.current.has(j.id)).map((j) => j.id);
    if (left.length === 0) await finish();
    else await send(left);
  }, [finish, send]);

  const reset = useCallback(() => {
    cancelled.current = true;
    session.current = null;
    setOpened(null);
    setNames([]);
    setJobs(new Map());
    setSendingSince(null);
    setPhase({ kind: "idle" });
  }, []);

  return { phase, names, session: opened, jobs, sendingSince, start, retry, reset, canRetry: opened !== null };
}
